import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar } from "lucide-react";

interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  author: string;
  category: string | null;
  tags: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  published_at: string | null;
  created_at: string;
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Blog | null>(null);
  const [related, setRelated] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  usePageMeta(
    post?.seo_title ?? post?.title ?? "Blog — Shattara AI",
    post?.seo_description ?? post?.excerpt ?? "Read this article on the Shattara AI blog.",
    { noIndex: notFound },
  );

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    supabase
      .from("blogs")
      .select("*")
      .eq("slug", slug)
      .eq("published", true)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setPost(data as Blog);
        setLoading(false);

        // Fetch related posts
        supabase
          .from("blogs")
          .select("id, title, slug, excerpt, category, published_at, created_at")
          .eq("published", true)
          .neq("slug", slug)
          .eq("category", data.category ?? "")
          .limit(3)
          .then(({ data: relData }) => setRelated((relData as Blog[]) ?? []));
      });
  }, [slug]);

  const publishedDate = post
    ? new Date(post.published_at ?? post.created_at).toISOString()
    : undefined;

  const structuredData = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.seo_description ?? post.excerpt,
        datePublished: publishedDate,
        dateModified: publishedDate,
        author: { "@type": "Person", name: post.author },
        publisher: {
          "@type": "Organization",
          name: "Shattara AI",
          logo: {
            "@type": "ImageObject",
            url: "https://shattaraai.com/og-image.png",
          },
        },
        mainEntityOfPage: `https://shattaraai.com/blog/${post.slug}`,
      }
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />
      <main id="main-content" className="flex-1">
        {structuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )}

        <article className="container mx-auto px-6 py-12 md:py-20 max-w-3xl">
          {loading ? (
            <div className="space-y-4 animate-pulse">
              <div className="h-8 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-64 bg-muted rounded mt-8" />
            </div>
          ) : notFound ? (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold text-foreground mb-4">
                Post not found
              </h1>
              <p className="text-muted-foreground mb-6">
                The article you're looking for doesn't exist or has been removed.
              </p>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="h-4 w-4" /> Back to Blog
              </Link>
            </div>
          ) : post ? (
            <>
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
              >
                <ArrowLeft className="h-4 w-4" /> All posts
              </Link>

              {post.category && (
                <Badge variant="secondary" className="mb-4">
                  {post.category}
                </Badge>
              )}

              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
                {post.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-10">
                <span>{post.author}</span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  <time>
                    {new Date(
                      post.published_at ?? post.created_at,
                    ).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </time>
                </span>
              </div>

              <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-a:text-primary">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {post.content}
                </ReactMarkdown>
              </div>

              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-border">
                  {post.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </article>

        {/* Related Posts */}
        {related.length > 0 && (
          <section className="border-t border-border py-12">
            <div className="container mx-auto px-6 max-w-3xl">
              <h2 className="text-xl font-semibold text-foreground mb-6">
                Related Articles
              </h2>
              <div className="grid gap-6 sm:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/blog/${r.slug}`}
                    className="group rounded-lg border border-border bg-card p-4 hover:border-primary/40 transition-all"
                  >
                    <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {r.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
